import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  AuthenticationActionType,
  IoniconsOutline,
  LoginAttempt,
  REQUIRED_VALIDATOR_KEY,
  SignAction,
  WidgetValidator,
} from '@pt/pt-models';
import { takeUntil } from 'rxjs/operators';
import { Observable, Subject, take } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { PasswordParamsResponse } from '@primatoo-ms-organization';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'pt-sign-in',
  templateUrl: './sign-in.component.html',
})
export class SignInComponent implements OnInit, OnDestroy, OnChanges {
  @Input() title: string;
  @Input() subtitle: string;
  @Input() loading: boolean;
  @Input() logo: string;
  @Input() domain: string;
  @Input() otpRequired: boolean;
  @Input() domainLabel: string;
  @Input() usernameLabel: string;
  @Input() passwordLabel: string;
  @Input() otpLabel: string;
  @Input() forgotPasswordLabel: string;
  @Input() cancelLabel: string;
  @Input() signInButtonLabel: string;
  @Input() formBackground: string = null;
  @Input() enabledReCaptcha: boolean;
  @Input() googleSiteKey: string;
  @Input() loginAttempt: LoginAttempt[];
  @Input() manageSystemLogin: boolean;
  @Input() passwordParamsResponse: PasswordParamsResponse;
  @Input() hasMicrosoftAuth: boolean;

  @Input() connectionAttempts: number;

  @Output() signAction = new EventEmitter<SignAction & { rcToken?: string }>();
  @Output() cancel = new EventEmitter();
  @Output() microsoftLogin = new EventEmitter();

  attemptsBeforeWarning: number;
  REQUIRED: WidgetValidator = {
    key: REQUIRED_VALIDATOR_KEY,
  } as WidgetValidator;
  warningMessage: Observable<string>;
  loginFailedMessage: Observable<string>;
  destroy$ = new Subject<boolean>();
  formGroup: FormGroup;
  IconWarning = IoniconsOutline.warning;

  constructor(
    private translateService: TranslateService,
    private route: ActivatedRoute) {
    this.route.queryParams.pipe(take(1)).subscribe((params) => {
      const err = params['err'];
      if (err) {
        this.warningMessage = this.translateService.stream('MESSAGE.LOGIN_FAILED');
      }
    });
  }

  static isSystemLogin(username: string): boolean {
    if (username) {
      return username[0] === '@';
    }
    return false;
  }

  ngOnInit(): void {
    this.attemptsBeforeWarning = this.connectionAttempts + 1;

    this.buildLoginFormGroup();

    this.formGroup.controls.username.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((username: string) => {
        this.checkUsername(username);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.enabledOtp && this.formGroup) {
      if (this.otpRequired) {
        this.formGroup.controls.otpCode.setValidators([Validators.required]);
      } else {
        this.formGroup.controls.otpCode.setValidators([]);
      }
      this.formGroup.controls.otpCode.setValue(null);
    }
    if (changes?.loginAttempt && this.formGroup) {
      const value = this.formGroup.controls.username.value;
      this.checkUsername(value);
      if (!this.warningMessage) {
        const userAttempts = this.loginAttempt?.find(
          (a) => a.username === value || '@' + a.username === value
        );
        if (userAttempts?.attempts > 0) {
          this.loginFailedMessage = this.translateService.stream(
            'MESSAGE.LOGIN_FAILED'
          );
        }
      }
    }
  }

  public ngOnDestroy() {
    this.destroy$.next(true);
  }

  public forgotPassword() {
    this.signAction.emit({
      action: AuthenticationActionType.FORGOT_PASSWORD,
      args: undefined,
    } as SignAction);
  }

  public buildLoginFormGroup() {
    this.formGroup = new FormGroup({
      domain: new FormControl(
        this.domain ? this.domain : '',
        this.domainLabel ? [Validators.required] : []
      ),
      username: new FormControl(
        null,
        this.usernameLabel ? [Validators.required] : []
      ),
      password: new FormControl(
        null,
        this.passwordLabel ? [Validators.required] : []
      ),
      otpCode: new FormControl(
        null,
        this.otpRequired ? [Validators.required] : []
      ),
    });
  }

  signIn() {
    const payload = this.formGroup.getRawValue();
    if (
      SignInComponent.isSystemLogin(payload.username) &&
      this.manageSystemLogin
    ) {
      payload.loginAsSystem = true;
      payload.username = payload.username.slice(1);
    }
    const signAction: SignAction = {
      action: AuthenticationActionType.SIGN_IN,
      payload,
    };
    this.signAction.emit(signAction);
  }

  private checkUsername(username: string) {
    const userAttempts = this.loginAttempt?.find(
      (a) => a.username === username || '@' + a.username === username
    );
    if (this.showWarning(userAttempts)) {
      // TODO: non mi piace molto questa soluzione, ideale un input ma con la gestione del parametro?
      this.warningMessage = this.translateService.stream(
        'MESSAGE.CONNECTION_ATTEMPTS_PASSED'
      );
      this.loginFailedMessage = null;
    } else {
      this.warningMessage = null;
      this.loginFailedMessage = null;
    }
  }

  private showWarning(userAttempts: LoginAttempt): boolean {
    if (userAttempts) {
      return userAttempts.attempts > this.attemptsBeforeWarning;
    }
    return false;
  }
}
